// Shared wait-time formatting + urgency classification, used by the COD
// inbox and detail panels. Centralized so 분/시간/일 thresholds + the
// urgency-color rules can be tuned in one place.

export function formatWait(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "-";
  if (seconds < 60)        return `${seconds}초`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)        return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)          return `${hours}시간 ${minutes % 60}분`;
  const days = Math.floor(hours / 24);
  return `${days}일 ${hours % 24}시간`;
}

// Per-priority wait threshold (seconds) past which the row is rendered
// with a heightened-urgency accent. Critical cases age faster than
// warning/normal — the numbers reflect operational SLA targets, not
// arbitrary buckets.
const URGENCY_THRESHOLDS = {
  critical: 30 * 60,         // 30분
  warning:  2 * 60 * 60,     // 2시간
  normal:   24 * 60 * 60,    // 1일
} as const;

export type Urgency = "calm" | "warn" | "alarm";

export function classifyUrgency(priority: string, waitedSeconds: number): Urgency {
  const threshold = URGENCY_THRESHOLDS[priority as keyof typeof URGENCY_THRESHOLDS]
                  ?? URGENCY_THRESHOLDS.normal;
  if (waitedSeconds >= threshold * 2)   return "alarm";
  if (waitedSeconds >= threshold)       return "warn";
  return "calm";
}

// Accent color tokens per urgency level. Resolves to CSS variables so the
// design system stays in control.
export const URGENCY_ACCENT: Record<Urgency, { dot: string; text: string }> = {
  calm:  { dot: "var(--ink-4)",   text: "var(--ink-3)" },
  warn:  { dot: "var(--amber-500)", text: "var(--amber-500)" },
  alarm: { dot: "var(--rose-500)",  text: "var(--rose-500)" },
};
